import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, TrashIcon, X } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useConfirmLineItemMatch,
  useRejectLineItem,
  useUndoRejectLineItem,
  useAddRemittanceLineItems,
} from '@/api/rpc/hooks/accounts-receivable/useArRemittances';
import { useArInvoice } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { InvoiceSearchCombobox } from './invoice-search-combobox';
import { InvoicePickerCombobox } from '@/components/accounts-receivable/collection-threads/InvoicePickerCombobox';
import { DecimalInput } from '@/components/common/DecimalInput';
import { Currency } from '@/components/common/Currency';
import { useToast } from 'src/context/ToastContext';
import { cn } from '@/lib/utils';
import type { RemittanceDetail } from '@erp/rpc';
import { T, useGT } from 'gt-react';

type LineItem = RemittanceDetail['lineItems'][number];

function MatchStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'matched':
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-800 border-green-200"
        >
          <T context="remittance line item match status: invoice confirmed matched">
            Confirmed
          </T>
        </Badge>
      );
    case 'disputed':
      return (
        <Badge
          variant="outline"
          className="bg-red-50 text-red-800 border-red-200"
        >
          <T context="remittance line item match status: matched but short-paid / disputed">
            Disputed
          </T>
        </Badge>
      );
    case 'auto_matched':
      return (
        <Badge
          variant="outline"
          className="bg-blue-50 text-blue-800 border-blue-200"
        >
          <T context="remittance line item match status: system auto-matched, awaiting confirmation">
            Auto-matched
          </T>
        </Badge>
      );
    case 'unmatched':
      return (
        <Badge variant="secondary">
          <T context="remittance line item match status: no invoice matched yet">
            Unmatched
          </T>
        </Badge>
      );
    case 'ambiguous':
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
          <T context="remittance line item match status: multiple possible invoice matches">
            Ambiguous
          </T>
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="text-muted-foreground">
          <T context="remittance line item match status: match rejected">
            Rejected
          </T>
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function LineItemActions({ lineItem }: { lineItem: LineItem }) {
  const [showInvoiceSearch, setShowInvoiceSearch] = useState(false);
  const confirmMatch = useConfirmLineItemMatch();
  const rejectLineItem = useRejectLineItem();
  const undoReject = useUndoRejectLineItem();

  const isActionPending =
    confirmMatch.isPending || rejectLineItem.isPending || undoReject.isPending;

  if (lineItem.matchStatus === 'matched') {
    return null;
  }

  if (lineItem.matchStatus === 'rejected') {
    return (
      <Button
        size="sm"
        variant="ghost"
        disabled={isActionPending}
        onClick={() => undoReject.mutate({ lineItemId: lineItem.id })}
      >
        <T context="undo reject remittance line item">Undo</T>
      </Button>
    );
  }

  // unmatched, ambiguous, or auto_matched
  const invoiceId = lineItem.invoiceId;

  return (
    <div className="flex items-center gap-1">
      {invoiceId != null ? (
        <Button
          size="sm"
          variant="default"
          disabled={isActionPending}
          onClick={() =>
            confirmMatch.mutate({
              lineItemId: lineItem.id,
              invoiceId,
            })
          }
        >
          <T context="confirm remittance line item match">Confirm</T>
        </Button>
      ) : showInvoiceSearch ? (
        <InvoiceSearchCombobox
          onSelect={(invoiceId) => {
            confirmMatch.mutate({
              lineItemId: lineItem.id,
              invoiceId,
            });
            setShowInvoiceSearch(false);
          }}
          onCancel={() => setShowInvoiceSearch(false)}
        />
      ) : (
        <Button
          size="sm"
          variant="outline"
          disabled={isActionPending}
          onClick={() => setShowInvoiceSearch(true)}
        >
          <T context="assign invoice to remittance line item">Assign Invoice</T>
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        className="text-destructive"
        disabled={isActionPending}
        onClick={() => rejectLineItem.mutate({ lineItemId: lineItem.id })}
      >
        <T context="reject remittance line item">Reject</T>
      </Button>
    </div>
  );
}

/**
 * Inline "new line" row pinned to the bottom of the table so a user can add
 * lines while reading the remittance email side-by-side. Picking an invoice seeds the
 * amount with its outstanding balance (overridable); a negative amount records
 * a deduction. `kind` and the stored sign are derived server-side.
 */
function AddLineItemRow({
  remittanceAdviceId,
  customerId,
  onCancel,
}: {
  remittanceAdviceId: string;
  customerId?: string;
  onCancel: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const addLineItems = useAddRemittanceLineItems();

  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [amount, setAmount] = useState<string | null>(null);

  const { invoice } = useArInvoice(invoiceId ?? undefined);
  const selectedInvoice = invoice && invoice.id === invoiceId ? invoice : null;

  // Seed the amount with the invoice's outstanding balance once, when the
  // freshly-picked invoice loads. Keyed on id via a ref so a background refetch
  // can't clobber an amount the user has since edited.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!invoiceId) {
      seededFor.current = null;
      return;
    }
    if (!selectedInvoice || seededFor.current === invoiceId) return;
    seededFor.current = invoiceId;
    setAmount(selectedInvoice.balanceDue);
  }, [invoiceId, selectedInvoice]);

  const amountNum = amount === null ? Number.NaN : Number(amount);
  const canAdd =
    invoiceId !== null &&
    amount !== null &&
    Number.isFinite(amountNum) &&
    amountNum !== 0 &&
    !addLineItems.isPending;

  function handleInvoiceChange(nextInvoiceId: string | null) {
    setInvoiceId(nextInvoiceId);
    // Clear the amount so it re-seeds from the newly-picked invoice's balance.
    setAmount(null);
  }

  async function handleAdd() {
    if (!canAdd || invoiceId === null || amount === null) return;
    try {
      await addLineItems.mutateAsync({
        remittanceAdviceId,
        lineItems: [{ invoiceId, statedAmount: amount }],
      });
      showToast({
        title: gt('Line item added', {
          context: 'toast title, remittance line item added',
        }),
        color: 'success',
        icon: 'check',
      });
      setInvoiceId(null);
      setAmount(null);
      seededFor.current = null;
    } catch (err) {
      showToast({
        title: gt('Add failed', {
          context: 'toast title, remittance line item add failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <TableRow className="bg-muted/40">
      <TableCell className="font-mono text-sm text-muted-foreground">
        {selectedInvoice?.invoiceNumber ?? '—'}
      </TableCell>
      <TableCell>
        <DecimalInput
          value={amount}
          onChange={setAmount}
          className="h-8"
          placeholder={gt('Amount', {
            context: 'placeholder, remittance line item amount',
          })}
        />
      </TableCell>
      <TableCell>
        <InvoicePickerCombobox
          customerId={customerId}
          value={invoiceId}
          onChange={handleInvoiceChange}
          placeholder={gt('Select invoice', {
            context: 'placeholder, remittance line invoice picker',
          })}
        />
      </TableCell>
      <TableCell className="text-sm">
        <Currency value={selectedInvoice?.balanceDue ?? null} fallback="--" />
      </TableCell>
      <TableCell>
        <Badge variant="secondary">
          <T context="remittance line item, unsaved new row">New</T>
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button size="sm" onClick={handleAdd} disabled={!canAdd}>
            <T context="button, add remittance line item">Add</T>
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-muted-foreground"
            disabled={addLineItems.isPending}
            onClick={onCancel}
            aria-label={gt('Cancel adding line item', {
              context: 'a11y label, cancel the inline add-line row',
            })}
          >
            <X className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function rowClassName(status: string): string {
  switch (status) {
    case 'unmatched':
    case 'ambiguous':
      return 'bg-amber-50/50';
    case 'auto_matched':
      return 'bg-blue-50/50';
    case 'rejected':
      return 'opacity-50';
    default:
      return '';
  }
}

interface RemittanceLineItemsTableProps {
  lineItems: LineItem[];
  /** When true (editable remittances), each row gets a delete control. */
  editable?: boolean;
  /**
   * When set alongside `editable`, a "+ Add line item" button reveals an inline
   * add-line row at the bottom of the table.
   */
  remittanceAdviceId?: string;
  /** Restricts the add-row invoice picker to this customer's invoices. */
  customerId?: string;
  onDelete?: (lineItemId: string) => void;
  deletingId?: string | null;
  /**
   * Line-item ids to badge "Disputed" (matched an invoice but short-paid),
   * independent of `matchStatus`. Optional; only the AR demo supplies it today.
   */
  disputedLineItemIds?: ReadonlySet<string>;
}

export function RemittanceLineItemsTable({
  lineItems,
  editable = false,
  remittanceAdviceId,
  customerId,
  onDelete,
  deletingId,
  disputedLineItemIds,
}: RemittanceLineItemsTableProps) {
  const gt = useGT();
  const [adding, setAdding] = useState(false);
  const canAddLines = editable && remittanceAdviceId != null;

  const addButton = canAddLines && !adding && (
    <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
      <Plus className="mr-1 size-4" weight="bold" />
      <T context="button, reveal inline add-line row">Add line item</T>
    </Button>
  );

  if (lineItems.length === 0 && !adding) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground py-4 text-center">
          <T context="no remittance line items">No line items</T>
        </div>
        {addButton}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[140px]">
              <T context="remittance line items table column header">
                Invoice Ref
              </T>
            </TableHead>
            <TableHead className="w-[100px]">
              <T context="remittance line items table column header, stated amount">
                Stated Amt
              </T>
            </TableHead>
            <TableHead className="w-[140px]">
              <T context="remittance line items table column header">
                Matched Invoice
              </T>
            </TableHead>
            <TableHead className="w-[100px]">
              <T context="remittance line items table column header">
                Balance Due
              </T>
            </TableHead>
            <TableHead className="w-[100px]">
              <T context="remittance line items table column header">Status</T>
            </TableHead>
            <TableHead>
              <T context="remittance line items table column header">Actions</T>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lineItems.map((li) => {
            const disputed = disputedLineItemIds?.has(li.id) ?? false;
            return (
              <TableRow
                key={li.id}
                className={cn(
                  rowClassName(li.matchStatus),
                  disputed && 'bg-red-50/50',
                )}
              >
                <TableCell className="font-mono text-sm">
                  {li.rawInvoiceRef}
                </TableCell>
                <TableCell className="text-sm">
                  <Currency value={li.statedAmount} fallback="--" />
                </TableCell>
                <TableCell className="text-sm">
                  {li.invoiceId && li.invoice?.invoiceNumber ? (
                    <Link
                      to="/accounts-receivable/invoices/$arInvoiceId"
                      params={{ arInvoiceId: li.invoiceId }}
                      viewTransition
                      className="text-blue-600 hover:underline"
                    >
                      {li.invoice.invoiceNumber}
                    </Link>
                  ) : (
                    (li.invoice?.invoiceNumber ?? '--')
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  <Currency
                    value={li.invoice?.balanceDue ?? null}
                    fallback="--"
                  />
                </TableCell>
                <TableCell>
                  <MatchStatusBadge
                    status={disputed ? 'disputed' : li.matchStatus}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <LineItemActions lineItem={li} />
                    {editable && onDelete && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-7 text-muted-foreground hover:text-destructive"
                        disabled={deletingId === li.id}
                        onClick={() => onDelete(li.id)}
                        aria-label={gt('Delete line item', {
                          context: 'a11y label, delete remittance line item',
                        })}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {adding && remittanceAdviceId && (
            <AddLineItemRow
              remittanceAdviceId={remittanceAdviceId}
              customerId={customerId}
              onCancel={() => setAdding(false)}
            />
          )}
        </TableBody>
      </Table>
      {addButton}
    </div>
  );
}
